import { A, useSearchParams } from '@solidjs/router'
import { Card } from 'solid-bootstrap'
import { FaSolidPencil, FaSolidPlus } from 'solid-icons/fa'
import { For, Show, Suspense, createSignal } from 'solid-js'
import { createMutable, produce } from 'solid-js/store'
import {
  type IndexMapQuery
} from '../../backend'
import AlertBag from '../../components/AlertBag'
import Breadcrumbs from '../../components/Breadcrumb/Breadcrumbs'
import DeleteButton from '../../components/Btn/DeleteButton'
import PublishButton from '../../components/Btn/PublishButton'
import CardText from '../../components/Card/CardText'
import CardTitle from '../../components/Card/CardTitle'
import DiskSelect from '../../components/DiskSelect'
import EngineContext from '../../components/EngineContext'
import Loader from '../../components/Loader'
import NewGameIcon from '../../components/MapEditor/NewGameIcon'
import MapShowCanvas3D from '../../components/MapShowCanvas3D'
import StateDiskText from '../../components/State/StateDiskText'
import MapSizeText from '../../components/Text/MapSizeText'
import EmSmallTextMuted from '../../components/Util/EmSmallTextMuted'
import createEngineForUI from '../../lib/core/engine/createEngineForUI'
import { Engine } from '../../lib/core/engine/Engine.type'
import { EngineMode } from '../../lib/core/engine/EngineMode/EngineMode.enum'
import { ProfileIdSchema } from '../../lib/core/ProfileId.type'
import {
  getStateMapDiskFromLocalStorage,
} from '../../lib/core/state/state_map_disk.helpers'
import { getDefaultStateMapDisk } from '../../lib/core/state/getDefaultStateMapDisk'
import mapDisplayName from '../../lib/core/state/mapDisplayName'
import { StateMapDisk } from '../../lib/core/state/state_map_disk.enum'
import { StateTypeMap } from '../../lib/core/state/stateTypeMetaList.type'
import parseIntOrNull from '../../lib/core/util/math/parseIntOrNull'
import createSetEngineAuthPlayerIdFromAuthProfileSignalEffect from '../../rx/effect/createSetEngineAuthPlayerIdFromAuthProfileSignalEffect'
import createValibotSafeParseMemo from '../../rx/memo/createValibotSafeParseMemo'
import createMapCollectionResource from '../../rx/resource/createMapCollectionResource'
import { getAuthProfile } from '../../rx/shared/profile/createAuthProfileSignal'
import getSessionUserId from '../../rx/shared/session/getSessionUserId'
import { createLoadingSignal, registerLoadingResource } from '../../rx/signal/create_loading_signal'
import { useEngineModeSignal } from '../../rx/signal/useEngineModeSignal'
import { createAlertBagStore, registerAlertBagResource } from '../../rx/store/AlertBag'
import type { Nullable } from '../../typescript'

const MAPS_DISK_LS_KEY = 'maps_disk'

function getMapRouteId(stateRow: {
  file_id?: string
  data: { map: { id?: number | null | undefined } }
  id?: number | null | undefined
}): string | number | null {
  return stateRow.file_id ?? stateRow.data.map.id ?? stateRow.id ?? null
}

const MapsIndexPage = () => {
  const loading = createLoadingSignal()
  const ab = createAlertBagStore()
  const [searchParams] = useSearchParams<Record<keyof IndexMapQuery, string>>()
  const ownerProfileId = createValibotSafeParseMemo(
    ProfileIdSchema,
    () => parseIntOrNull(searchParams.owner_profile_id)
  )
  const [selectedDisk, setSelectedDisk] = createSignal<StateMapDisk>(
    getStateMapDiskFromLocalStorage(MAPS_DISK_LS_KEY, getDefaultStateMapDisk, import.meta.env.DEV)
  )

  const mapCollectionResource = createMapCollectionResource(() => {
    return {
      owner_profile_id: ownerProfileId().value,
      disk: selectedDisk()
    }
  })
  registerLoadingResource(loading, mapCollectionResource)
  registerAlertBagResource(ab, mapCollectionResource)
  const [mapList] = mapCollectionResource

  const engine: Engine = createMutable(createEngineForUI())

  const [, setEngineMode] = useEngineModeSignal(engine)

  setEngineMode(EngineMode.MapPreview)

  createSetEngineAuthPlayerIdFromAuthProfileSignalEffect(engine, getAuthProfile)

  return (
    <EngineContext.Provider value={engine}>
      <div class="pagec">
        <Breadcrumbs />
        <h2>Map List</h2>
        <DiskSelect
          value={selectedDisk()}
          onChange={setSelectedDisk}
          localStorageKey={MAPS_DISK_LS_KEY}
          allowWorkspace={import.meta.env.DEV}
        />
        <div class="btn-group">
          <Show when={getSessionUserId() && ownerProfileId().value !== getSessionUserId()}>
            <A href={`/maps?owner_profile_id=${getSessionUserId()}`} class="btn btn-primary">
              My Maps
            </A>
          </Show>
          <Show when={!(getSessionUserId() && ownerProfileId().value !== getSessionUserId())}>
            <A href="/maps" class="btn btn-primary">
              All Published Maps
            </A>
          </Show>
          <A href="/maps/create" class="btn btn-success">
            <FaSolidPlus /> New Map
          </A>
        </div>
        <Loader signal={loading} />
        <AlertBag store={ab} />
        <Suspense fallback={<div class="text-muted">Loading...</div>}>
          <For each={mapList()} fallback={<div class="text-muted">Empty List</div>}>
            {(stateRow) => {
              const [map, setMap] = createSignal(stateRow)
              const stateId = (): Nullable<number | string> => getMapRouteId(map())
              const stateHrefId = (): string => encodeURIComponent(String(stateId()))

              return <Card>
                <div class="card-body clearfix">
                  <div class="float-end">
                    <MapShowCanvas3D state={map().data} width={400} height={300} />
                  </div>
                  <CardTitle>
                    <A href={`/map?map_id=${stateHrefId()}&disk=${map().data.map.disk}`}
                      aria-label="Goto Map"
                      title="Goto Map"
                    >{mapDisplayName(map().data)}</A>
                    {' '}
                    <A href={`/maps/edit?map_id=${stateHrefId()}&disk=${map().data.map.disk}`}
                      class="btn btn-dark btn-sm border-white"
                      aria-label="Edit Map"
                      title="Edit Map"
                    >
                      <FaSolidPencil />
                    </A>
                    <StateDiskText disk={map().data.map.disk} />
                    {' '}
                    <DeleteButton
                      st={StateTypeMap} disabled={loading()}
                      item={stateRow} ab={ab}
                      onChange={(newValue) => {
                        setMap(produce((stateRow) => {
                          stateRow.deleted_at = newValue
                        }))
                      }}
                    />
                    {' '}
                    <PublishButton
                      st={StateTypeMap} disabled={loading()}
                      item={stateRow} ab={ab}
                      onChange={(newValue) => {
                        console.log('newValue', newValue)
                        setMap(produce((stateRow) => {
                          stateRow.published_at = newValue
                        }))
                      }}
                    />
                    <Show when={!map().file_id}>
                      {' '}
                      <A href={`/lobby/create?map_id=${stateHrefId()}`}
                        class="btn btn-success btn-sm"
                        aria-label="Edit Map"
                        title="Edit Map"
                      >
                        <NewGameIcon />
                        {' '}
                        New Game
                      </A>
                    </Show>
                  </CardTitle>
                  <CardText>
                    {map().data.map.bio || EmSmallTextMuted('no description')}
                  </CardText>
                  <CardText>
                    Size: <MapSizeText state={map().data} />
                  </CardText>
                </div>
              </Card>
            }}
          </For>
        </Suspense>
      </div>
    </EngineContext.Provider>
  )
}

export default MapsIndexPage
